/**
 * Receiver addresses for the proforma order dialog: the sources the address can be taken from
 * (brandhouse, shop, logistics partner or typed in), and how each maps onto the order's address fields.
 */
import {
   Order2ShippingAddressInput,
   ProformaAddressInfoFragment,
   ProformaLogisticsPartnerAddressFragment
} from "../../Queries";

export enum ProformaAddressSource {
   Brandhouse = 'brandhouse',
   Shop = 'shop',
   LogisticsPartner = 'logisticsPartner',
   Custom = 'custom'
}

/** The order address fields the dialog edits, all plain strings so the form inputs always have a value. */
export type ProformaAddressFields = Record<keyof Omit<Order2ShippingAddressInput, '_id'>, string>;

/** Which address each source gives. */
export type ProformaAddressBook = Record<ProformaAddressSource, ProformaAddressFields>;

export const emptyProformaAddress: ProformaAddressFields = {
   name: '',
   company: '',
   add1: '',
   add2: '',
   zipCode: '',
   city: '',
   country: '',
   countryCode: '',
   mail: '',
   phone: ''
};

/**
 * A brandhouse or shop address. The address name becomes the company, and the attention line, when set,
 * the person receiving it. Only a country code is stored, so it fills the country as well.
 */
export const fromAddressInfo = (address?: ProformaAddressInfoFragment | null): ProformaAddressFields => {
   if (!address) {
      return emptyProformaAddress;
   }
   return {
      name: address.attention || address.name || '',
      company: address.name || '',
      add1: address.add1 || '',
      add2: address.add2 || '',
      zipCode: address.zipCode || '',
      city: address.city || '',
      country: address.countryCode || '',
      countryCode: address.countryCode || '',
      mail: address.mail || '',
      phone: address.phone || ''
   };
};

/** True for a logistics partner field holding a {{wildcard}} template, which Core fills in only on inbound labels. */
const isLabelTemplate = (value?: string | null) => !!value?.includes('{{');

/**
 * The logistics partner's own address. Its attention line is left out, and so is a templated second
 * address line, because both are templates for labels on parcels sent to the warehouse.
 */
export const fromLogisticsPartner = (partner?: ProformaLogisticsPartnerAddressFragment | null): ProformaAddressFields => {
   if (!partner) {
      return emptyProformaAddress;
   }
   return {
      name: partner.name || '',
      company: partner.name || '',
      add1: partner.address || '',
      add2: isLabelTemplate(partner.address2) ? '' : partner.address2 || '',
      zipCode: partner.zipCode || '',
      city: partner.city || '',
      country: partner.country || partner.countryCode || '',
      countryCode: partner.countryCode || '',
      mail: partner.mail || '',
      phone: partner.phone || ''
   };
};

/** Collects the address of every source, so switching source in the dialog is a plain lookup. A custom address starts empty. */
export const buildProformaAddressBook = (
   brandHouse?: ProformaAddressInfoFragment | null,
   shop?: ProformaAddressInfoFragment | null,
   logisticsPartner?: ProformaLogisticsPartnerAddressFragment | null
): ProformaAddressBook => ({
   [ProformaAddressSource.Brandhouse]: fromAddressInfo(brandHouse),
   [ProformaAddressSource.Shop]: fromAddressInfo(shop),
   [ProformaAddressSource.LogisticsPartner]: fromLogisticsPartner(logisticsPartner),
   [ProformaAddressSource.Custom]: emptyProformaAddress
});
